/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback } from 'react';
import {
  FlatList,
  RefreshControl,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { usePendingTasks, type PendingTaskRow } from '../screens/hooks/usePendingTasks';

const KIND_ICON: Record<string, keyof typeof Ionicons.glyphMap> = {
  call: 'call-outline',
  message: 'chatbubble-outline',
  visit: 'footsteps-outline',
  task: 'checkmark-circle-outline',
};

const KIND_LABEL: Record<string, string> = {
  call: 'Llamar',
  message: 'Mensaje',
  visit: 'Visita',
  task: 'Tarea',
};

function formatDue(iso: string | null): string {
  if (!iso) return 'Sin fecha';
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const due = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);
  const dateStr = d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Mañana';
  if (diffDays === -1) return 'Ayer';
  if (diffDays < 0) return `Venció ${dateStr}`;
  return dateStr;
}

function isOverdue(task: PendingTaskRow): boolean {
  if (!task.dueDate) return false;
  return new Date(task.dueDate).getTime() < Date.now();
}

export function FollowUpInboxScreen() {
  const { canEdit } = useBranchPermission('Customers');
  const canEditContact = useModulePermission('Customers', 'edit_contact');
  const canEditCustomer = canEdit || canEditContact;
  const canManageTasks =
    useModulePermission('Customers', 'manage_tasks') || canEditCustomer;

  const {
    tasks,
    count,
    loading,
    page,
    hasMore,
    loadPage,
    reload,
    completeTask,
    archiveTask,
  } = usePendingTasks(true);

  useFocusEffect(
    useCallback(() => {
      reload();
    }, [reload]),
  );

  const renderItem = ({ item }: { item: PendingTaskRow }) => {
    const overdue = isOverdue(item);
    return (
      <View style={styles.taskCard} testID="followup.task">
        <View style={styles.taskMain}>
          <Ionicons
            name={KIND_ICON[item.kind] ?? 'checkmark-circle-outline'}
            size={18}
            color={ticketColors.ink2}
          />
          <View style={styles.taskBody}>
            <Text style={styles.taskTitle} numberOfLines={2}>{item.title}</Text>
            <View style={styles.taskMeta}>
              <Text style={styles.taskCustomer} numberOfLines={1}>{item.customerName || 'Cliente'}</Text>
              <Text style={[styles.taskDue, overdue && styles.taskDueOverdue]}>
                {formatDue(item.dueDate)}
              </Text>
              <Text style={styles.taskKind}>{KIND_LABEL[item.kind] ?? 'Tarea'}</Text>
            </View>
          </View>
        </View>
        <View style={styles.taskActions}>
          {canManageTasks ? (
            <>
              <TouchableOpacity
                onPress={() => completeTask(item.id)}
                accessibilityRole="button"
                accessibilityLabel="Marcar como hecha"
                testID={`followup.task.complete.${item.id}`}
              >
                <Ionicons name="checkmark-circle" size={22} color={ticketColors.success} />
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => archiveTask(item.id)}
                accessibilityRole="button"
                accessibilityLabel="Archivar tarea"
                testID={`followup.task.archive.${item.id}`}
              >
                <Ionicons name="trash-outline" size={16} color={ticketColors.ink3} />
              </TouchableOpacity>
            </>
          ) : null}
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container} edges={['bottom']} testID="followup.inbox">
      <View style={styles.headerRow}>
        <Text style={styles.headerTitle}>Seguimiento</Text>
        {count > 0 ? (
          <View style={styles.countBadge} testID="followup.count">
            <Text style={styles.countBadgeText}>{count}</Text>
          </View>
        ) : null}
      </View>

      <FlatList
        data={tasks}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={reload} />}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          loading ? null : (
            <View style={styles.emptyState} testID="followup.empty">
              <Ionicons name="checkmark-done-circle-outline" size={48} color={ticketColors.ink3} />
              <Text style={styles.emptyText}>Sin tareas pendientes.</Text>
              <Text style={styles.emptySub}>Creá tareas desde la ficha de un cliente.</Text>
            </View>
          )
        }
        ListFooterComponent={
          (hasMore || page > 0) ? (
            <View style={styles.pagerRow} testID="followup.pager">
              <TouchableOpacity
                style={[styles.pagerBtn, page === 0 && styles.pagerBtnDisabled]}
                onPress={() => page > 0 && loadPage(page - 1)}
                disabled={page === 0 || loading}
              >
                <Ionicons name="chevron-back" size={16} color={page === 0 ? ticketColors.ink3 : ticketColors.ink} />
                <Text style={[styles.pagerText, page === 0 && styles.pagerTextDisabled]}>Anterior</Text>
              </TouchableOpacity>
              <Text style={styles.pagerInfo}>Pág. {page + 1}</Text>
              <TouchableOpacity
                style={[styles.pagerBtn, !hasMore && styles.pagerBtnDisabled]}
                onPress={() => hasMore && loadPage(page + 1)}
                disabled={!hasMore || loading}
              >
                <Text style={[styles.pagerText, !hasMore && styles.pagerTextDisabled]}>Siguiente</Text>
                <Ionicons name="chevron-forward" size={16} color={!hasMore ? ticketColors.ink3 : ticketColors.ink} />
              </TouchableOpacity>
            </View>
          ) : null
        }
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ticketColors.bg,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  countBadge: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
    minWidth: 22,
    alignItems: 'center',
  },
  countBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#fff',
  },
  listContent: {
    paddingBottom: spacing.xl,
  },
  taskCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.xs,
    ...ticketSurfaces.paperSheet,
  },
  taskMain: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    flex: 1,
  },
  taskBody: {
    flex: 1,
  },
  taskTitle: {
    fontSize: 15,
    color: ticketColors.ink,
    marginBottom: 3,
    fontWeight: '500',
  },
  taskMeta: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  taskCustomer: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontWeight: '500',
    maxWidth: 160,
  },
  taskDue: {
    fontSize: 12,
    color: ticketColors.ink3,
  },
  taskDueOverdue: {
    color: ticketColors.bad,
    fontWeight: '700',
  },
  taskKind: {
    fontSize: 11,
    color: ticketColors.ink3,
    backgroundColor: ticketColors.ruleSoft,
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 8,
    overflow: 'hidden',
  },
  taskActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingLeft: spacing.xs,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: spacing.xl * 2,
  },
  emptyText: {
    fontSize: 16,
    color: ticketColors.ink2,
    fontWeight: '600',
    marginTop: spacing.sm,
  },
  emptySub: {
    fontSize: 13,
    color: ticketColors.ink3,
    marginTop: 4,
  },
  pagerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: spacing.sm,
    marginTop: spacing.xs,
  },
  pagerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.xs,
    paddingVertical: 6,
  },
  pagerBtnDisabled: {
    opacity: 0.4,
  },
  pagerText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  pagerTextDisabled: {
    color: ticketColors.ink3,
  },
  pagerInfo: {
    fontSize: 13,
    color: ticketColors.ink3,
    fontWeight: '500',
  },
});
